import React, { useEffect, useRef, useState } from 'react';
import { useOutsideClickHandler } from '@utils/hooks/useOutsideClickHandler';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { SearchInput } from '../HierarchicalMultiSelect/search';

export type SelectOption = {
  label: string;
  value: string;
};

interface SearchableSelectProps {
  name: string;
  options: SelectOption[];
  className?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  wrapperClassName?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchTermValue?: string;
  searchTermOnChange?: (value: string) => void;
  disabled?: boolean;
  clearable?: boolean;
  label?: string;
  error?: string;
  loading?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  name,
  options,
  className,
  placeholder = 'Select an option',
  value,
  wrapperClassName,
  onChange,
  searchable = true,
  searchPlaceholder = 'Search...',
  searchTermValue,
  searchTermOnChange,
  disabled = false,
  clearable = false,
  label,
  error,
  loading = false
}) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(value ?? '');
  const [searchTerm, setSearchTerm] = useState<string>(searchTermValue ?? '');

  // Get selected option label
  const selectedOption = options.find(option => option.value === selectedValue);
  const displayText = selectedOption?.label || placeholder;

  // Filter options based on search term
  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const toggleDropdown = () => {
    if (disabled || loading) return;

    setIsOpen(!isOpen);
    // Focus search input when dropdown opens
    if (!isOpen && searchable) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (searchTermOnChange) {
      searchTermOnChange(e.target.value);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (searchTermOnChange) {
      searchTermOnChange('');
    }
  };

  const handleOptionClick = (optionValue: string) => {
    setSelectedValue(optionValue);
    setIsOpen(false);
    setSearchTerm(''); // Clear search when selection is made

    if (onChange) {
      onChange(optionValue);
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue('');
    if (onChange) {
      onChange('');
    }
  };

  useEffect(() => {
    if (value !== undefined && value !== selectedValue) {
      setSelectedValue(value);
    }
  }, [value]);

  useOutsideClickHandler(menuRef, () => {
    setIsOpen(false);
    setSearchTerm(''); // Clear search when closing dropdown
  });

  return (
    <div className={`relative w-full ${wrapperClassName ?? ''}`}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative inline-block text-left w-full" ref={menuRef}>
        <div>
          <span className="cursor-pointer rounded-md shadow-sm w-full">
            <Button
              onClick={toggleDropdown}
              type="button"
              btnStyle="link"
              disabled={disabled || loading}
              className={`inline-flex justify-between w-full px-4 py-2 text-sm bg-[var(--na-primary-lite)] primary-font ${
                selectedValue ? 'primary-color-grey-4' : 'text-gray-400'
              } border !border-[var(--na-primary-color)] rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 ${
                disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
              } ${className ?? ''}`}>
              <span className="truncate">{displayText}</span>
              <div className="flex items-center ml-2">
                {loading && (
                  <svg
                    className="animate-spin h-4 w-4 mr-2 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {clearable && selectedValue && !disabled && !loading && (
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    className="mr-2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear selection">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    isOpen ? 'transform rotate-180' : ''
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M6.293 9.293a1 1 0 011.414 0L10 11.586l2.293-2.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </Button>
          </span>
        </div>
        {isOpen && (
          <div className="absolute z-[200] w-full mt-2 py-3 origin-top-right bg-white rounded-md shadow-lg dropdown-height">
            {searchable && (
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                onClear={clearSearch}
                placeholder={searchPlaceholder}
              />
            )}
            <div className="grid grid-cols-1 gap-1 max-h-60 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center px-4 py-8">
                  <svg
                    className="animate-spin h-8 w-8 text-blue-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="ml-2 text-sm text-gray-600">Loading...</span>
                </div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map(item => (
                  <div
                    key={item.value}
                    onClick={() => handleOptionClick(item.value)}
                    className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors ${
                      selectedValue === item.value
                        ? 'bg-blue-50 text-blue-600 font-medium'
                        : 'text-gray-900'
                    }`}>
                    <span className="truncate capitalize">{item.label}</span>
                    {selectedValue === item.value && (
                      <svg
                        className="w-4 h-4 ml-auto"
                        fill="currentColor"
                        viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  {t('noOptionsFound')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <div className="error-msg text-sm pt-1">{error}</div>}
    </div>
  );
};
